import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-autotable';
import {
  AnalyticsOverviewResponse,
  AnalyticsSection,
  ANALYTICS_SECTION_LABELS,
  ANALYTICS_SECTIONS,
  MetricResult,
  MetricTrend,
} from '../types/adminAnalytics.ts';
import {
  formatDeltaPercent,
  formatDeltaValue,
  trendLabel,
} from './adminAnalyticsHelpers.ts';

const BRAND_COLOR = [35, 75, 162] as const;
const CHART_CAPTURE_SELECTOR = '[data-analytics-export-chart]';
const DEFAULT_EXPORT_ROOT_ID = 'admin-analytics-export-root';

type TrendStyle = {
  bg: [number, number, number];
  text: [number, number, number];
};

function getTrendStyle(trend: MetricTrend): TrendStyle {
  switch (trend) {
    case 'up':
      return { bg: [220, 252, 231], text: [21, 128, 61] };
    case 'down':
      return { bg: [254, 226, 226], text: [185, 28, 28] };
    default:
      return { bg: [241, 245, 249], text: [71, 85, 105] };
  }
}

function drawTrendIcon(
  doc: jsPDF,
  centerX: number,
  centerY: number,
  trend: MetricTrend,
  color: [number, number, number]
): void {
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(0.45);
  const half = 1.4;

  if (trend === 'up') {
    doc.line(centerX, centerY + half, centerX, centerY - half);
    doc.line(centerX - half, centerY - 0.2, centerX, centerY - half);
    doc.line(centerX + half, centerY - 0.2, centerX, centerY - half);
    return;
  }

  if (trend === 'down') {
    doc.line(centerX, centerY - half, centerX, centerY + half);
    doc.line(centerX - half, centerY + 0.2, centerX, centerY + half);
    doc.line(centerX + half, centerY + 0.2, centerX, centerY + half);
    return;
  }

  doc.line(centerX - half, centerY, centerX + half, centerY);
}

function formatMetricNumber(value: number): string {
  return value.toLocaleString('es-AR');
}

function drawTrendBadge(
  doc: jsPDF,
  x: number,
  y: number,
  trend: MetricTrend,
  deltaPercent: number | null
): number {
  const style = getTrendStyle(trend);
  const percentText = formatDeltaPercent(deltaPercent);
  const iconSlot = 4;
  const iconGap = 1.5;
  const padX = 2.5;
  const badgeH = 6;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const textWidth = doc.getTextWidth(percentText);
  const badgeW = padX * 2 + iconSlot + iconGap + textWidth;
  const badgeX = x - badgeW;

  doc.setFillColor(style.bg[0], style.bg[1], style.bg[2]);
  doc.setDrawColor(style.bg[0], style.bg[1], style.bg[2]);
  doc.roundedRect(badgeX, y - 3.5, badgeW, badgeH, 1.2, 1.2, 'F');

  drawTrendIcon(doc, badgeX + padX + iconSlot / 2, y + 0.2, trend, style.text);

  doc.setTextColor(style.text[0], style.text[1], style.text[2]);
  doc.text(percentText, badgeX + padX + iconSlot + iconGap, y + 0.5);
  return badgeW;
}

function drawKpiCard(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  metric: MetricResult
): void {
  const pad = 3;
  const innerRight = x + width - pad;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, width, height, 2, 2, 'FD');

  const badgeReserve = drawTrendBadge(
    doc,
    innerRight,
    y + pad + 4,
    metric.trend,
    metric.deltaPercent
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(85, 85, 85);
  const labelMaxWidth = width - pad * 2 - badgeReserve - 2;
  const labelLines = doc.splitTextToSize(metric.label, Math.max(labelMaxWidth, 20));
  doc.text(labelLines.slice(0, 2), x + pad, y + pad + 4);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text(formatMetricNumber(metric.current), x + pad, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(136, 136, 136);
  const compareText = `vs período anterior: ${formatMetricNumber(metric.previous)} (${formatDeltaValue(metric.delta)})`;
  const compareLines = doc.splitTextToSize(compareText, width - pad * 2);
  doc.text(compareLines.slice(0, 2), x + pad, y + 20);

  const dimensionEntries = Object.entries(metric.dimensions ?? {}).filter(
    ([, value]) => value
  );
  if (dimensionEntries.length === 0) return;

  let chipX = x + pad;
  let chipY = y + height - 5;
  const chipGap = 2;
  doc.setFontSize(6.5);

  dimensionEntries.forEach(([key, value]) => {
    const chipText = `${key}: ${value}`;
    const chipW = doc.getTextWidth(chipText) + 3;
    if (chipX + chipW > x + width - pad) {
      chipX = x + pad;
      chipY -= 4.5;
    }
    if (chipY < y + 24) return;

    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(241, 245, 249);
    doc.roundedRect(chipX, chipY - 3, chipW, 4.5, 0.8, 0.8, 'F');
    doc.setTextColor(71, 85, 105);
    doc.text(chipText, chipX + 1.5, chipY);
    chipX += chipW + chipGap;
  });
}

function addKpiCardsToPdf(
  doc: jsPDF,
  overview: AnalyticsOverviewResponse,
  sectionKeys: AnalyticsSection[],
  startY: number,
  margin: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  const cardsPerRow = 3;
  const gap = 5;
  const cardWidth = (contentWidth - gap * (cardsPerRow - 1)) / cardsPerRow;
  const cardHeight = 30;
  const sectionGap = 8;
  const rowGap = 5;

  let y = startY;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text('Indicadores (KPI)', margin, y + 4);
  y += 10;

  const sectionsToExport = sectionKeys.filter(
    (key) => (overview.sections[key]?.metrics?.length ?? 0) > 0
  );

  sectionsToExport.forEach((sectionKey) => {
    const metrics = overview.sections[sectionKey]!.metrics;
    const sectionTitle = ANALYTICS_SECTION_LABELS[sectionKey];

    if (y + 12 + cardHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.text(sectionTitle, margin, y + 5);
    doc.setDrawColor(232, 237, 243);
    doc.setLineWidth(0.35);
    doc.line(margin, y + 7.5, margin + contentWidth, y + 7.5);
    y += 10;

    let col = 0;
    let rowY = y;

    metrics.forEach((metric) => {
      if (col === 0 && rowY + cardHeight > pageHeight - margin) {
        doc.addPage();
        rowY = margin;
      }

      const cardX = margin + col * (cardWidth + gap);
      drawKpiCard(doc, cardX, rowY, cardWidth, cardHeight, metric);

      col += 1;
      if (col >= cardsPerRow) {
        col = 0;
        rowY += cardHeight + rowGap;
      }
    });

    if (col > 0) {
      rowY += cardHeight + rowGap;
    }

    y = rowY + sectionGap;
  });

  return y;
}

type CapturedImage = {
  dataUrl: string;
  width: number;
  height: number;
};

async function captureChartImages(exportRootId: string): Promise<CapturedImage[]> {
  const root = document.getElementById(exportRootId);
  if (!root) return [];

  const nodes = Array.from(root.querySelectorAll(CHART_CAPTURE_SELECTOR));
  const images: CapturedImage[] = [];

  for (const node of nodes) {
    const canvas = await html2canvas(node as HTMLElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
    });
    images.push({
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    });
  }

  return images;
}

function addChartImagesToPdf(
  doc: jsPDF,
  images: CapturedImage[],
  startY: number,
  margin: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  let y = startY;

  images.forEach((image) => {
    const imgHeight = (image.height * contentWidth) / image.width;
    if (y + imgHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
    doc.addImage(image.dataUrl, 'PNG', margin, y, contentWidth, imgHeight);
    y += imgHeight + 10;
  });

  return y;
}

export async function saveAdminAnalyticsPdf(
  overview: AnalyticsOverviewResponse,
  exportRootId: string = DEFAULT_EXPORT_ROOT_ID,
  sectionKeys: AnalyticsSection[] = ANALYTICS_SECTIONS
): Promise<void> {
  await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
  await new Promise((resolve) => setTimeout(resolve, 150));

  const chartImages = await captureChartImages(exportRootId);
  const { meta, rows } = overview.report;
  const doc = new jsPDF({ orientation: 'landscape' });
  const margin = 14;
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(16);
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text(meta.title, margin, 16);

  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Período: ${meta.periodLabel}`, margin, 24);
  doc.text(`Comparación: ${meta.comparePeriodLabel}`, margin, 30);
  doc.text(
    `Generado: ${new Date(meta.generatedAt).toLocaleString('es-AR')}`,
    margin,
    36
  );

  const sectionKeySet = new Set(sectionKeys);
  const exportRows = rows.filter((row) => sectionKeySet.has(row.section));

  let cursorY = addKpiCardsToPdf(doc, overview, sectionKeys, 42, margin);
  cursorY += 4;

  if (chartImages.length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
    doc.text('Gráficos comparativos', margin, cursorY + 4);
    cursorY += 10;
    cursorY = addChartImagesToPdf(doc, chartImages, cursorY, margin);
  }

  if (cursorY + 40 > pageHeight - margin) {
    doc.addPage();
    cursorY = margin;
  }

  doc.setFontSize(11);
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
  doc.text('Detalle de métricas', margin, cursorY + 4);

  const tableBody = exportRows.map((row) => [
    row.sectionLabel,
    row.metricLabel,
    String(row.current),
    String(row.previous),
    String(row.delta),
    formatDeltaPercent(row.deltaPercent),
    trendLabel(row.trend),
  ]);

  (doc as any).autoTable({
    startY: cursorY + 8,
    head: [['Sección', 'Métrica', 'Actual', 'Anterior', 'Delta', '%', 'Tendencia']],
    body: tableBody,
    styles: { fontSize: 8 },
    headStyles: { fillColor: BRAND_COLOR },
    margin: { left: margin, right: margin },
    pageBreak: 'auto',
  });

  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`edify-analytics-${dateStamp}.pdf`);
}

export const ADMIN_ANALYTICS_EXPORT_ROOT_ID = DEFAULT_EXPORT_ROOT_ID;
